/**
 * Sold Card Carousel → Lot Highlight Plugin for Native Interactive Site Map (ISM)
 *
 * Mobile-only mirror of cardLotHighlight.plugin.js: as the user swipes the bottom sheet's
 * one-at-a-time Sold carousel (services/listViewContentService.js#renderSoldHomeCards's
 * `scrollable && isMobile` case), the lot the centered card belongs to is highlighted on the map
 * the same way a desktop card hover does — both feed the same cardLotHovered$ Subject consumed by
 * lotInteraction.plugin.js.
 *
 * Two independent triggers recompute the centered card, since neither alone covers every way the
 * carousel can change:
 * - A MutationObserver on [data-panel] catches content swaps (chip switched to Sold, a lot
 *   selected — reordering selectSoldHomesWithSimilarHomesFirst's result — or the carousel appearing
 *   for the first time), all of which reset scrollLeft to 0 without firing a scroll event.
 * - A capture-phase, debounced scroll listener (handlers/scrollHandlers.js — the same pattern
 *   tracking.plugin.js's cardsScrollHandlers already uses to hear scroll from the
 *   innerHTML-replaced carousel div) catches the user's own swipe gesture.
 *
 * Both funnel into the same recomputeActiveCard(), which is a no-op whenever the scrollable Sold
 * carousel isn't currently in the DOM (desktop's plain vertical list, a different chip, or the
 * uncapped VIEW_SOLD_FULL_LIST "See All" screen never render the --scroll class this looks for).
 *
 * DOM lookup/geometry itself lives in handlers/soldCardCarouselHandlers.js, not here — this
 * plugin only owns lifecycle, fastdom scheduling, and the resolved sitePlanLotId -> Subject emit.
 *
 * @module nativeInteractiveSiteMap/plugins/soldCardCarouselHighlight.plugin
 */
import fastdom from 'fastdom';
import createScrollHandlers from '../handlers/scrollHandlers';
import createSoldCardCarouselHandlers from '../handlers/soldCardCarouselHandlers';
import { selectSoldLotsById } from '../redux/selectors';

export default function createSoldCardCarouselHighlightPlugin() {
  const state = {
    panel: null,
    scrollHandlers: null,
    mutationObserver: null,
    carouselHandlers: createSoldCardCarouselHandlers(),
    getState: null,
    cardLotHovered$: null,
    recomputeRequestId: 0,
  };

  function recomputeActiveCard() {
    state.recomputeRequestId += 1;
    const requestId = state.recomputeRequestId;

    // All getBoundingClientRect reads (container + every card) batched in one measure pass,
    // rather than interleaved with whatever else is about to mutate the DOM this frame.
    fastdom.measure(() => {
      const scrollContainer = state.panel && state.carouselHandlers.findScrollContainer(state.panel);
      const lotId = scrollContainer ? state.carouselHandlers.findActiveLotId(scrollContainer) : null;

      if (requestId !== state.recomputeRequestId || !state.cardLotHovered$) {
        return;
      }

      const sitePlanLotId = lotId ? selectSoldLotsById(state.getState())[lotId]?.sitePlanLotId : null;
      state.cardLotHovered$.next(sitePlanLotId ? new Set([sitePlanLotId]) : null);
    });
  }

  return {
    name: 'sold-card-carousel-highlight',

    async start(context) {
      if (!context.state.isMobile) {
        return;
      }

      state.panel = context.root.querySelector('[data-panel]');
      if (!state.panel) {
        return;
      }

      state.getState = context.services.getState;
      state.cardLotHovered$ = context.services.cardLotHovered$;

      state.scrollHandlers = createScrollHandlers();
      state.scrollHandlers.bind(state.panel, recomputeActiveCard);

      state.mutationObserver = new MutationObserver(recomputeActiveCard);
      state.mutationObserver.observe(state.panel, { childList: true, subtree: true });

      // Highlights the default first card immediately, before any swipe.
      recomputeActiveCard();
    },

    async stop() {
      state.scrollHandlers?.unbind();
      state.scrollHandlers = null;

      state.mutationObserver?.disconnect();
      state.mutationObserver = null;

      state.panel = null;
      state.getState = null;
      state.cardLotHovered$ = null;
    },
  };
}
